import * as THREE from "three";
import { ActorState } from "./ActorState";
import EventEmitter from "events";
import { PosableMapObject, Twist, Vec3, Vec4 } from "../../../models/SyntheticMap";
import { UIService } from "../../../api/UIService";
import { BaseSensor } from "../sensors/BaseSensor";
import { BaseThreeDFactory } from "../BaseThreeDFactory";
import { GLTFResult } from "../../../api/threed";
import { RobocoreClient } from "../../../api/robocore/RobocoreClient";
import { ObjectStateManager } from "../../../api/robocore/BaseDataChannel";
import { normalizeAngleToPi } from "../../../utils/spatial";
import { ActorPathVisualizer } from "./ActorPathVisualizer";

export enum ActorEvents {
  StateChange = "stateChange",
  SensorChange = "sensorChange",
}

export interface CameraConfig {
  position: THREE.Vector3;
  rotation: THREE.Euler;
  lookAt: THREE.Vector3;
  parent?: THREE.Object3D;
  fov?: number; // field of view in degrees
}

export interface KeyFrame {
  position?: Vec3;
  rotationQ?: THREE.Quaternion;
  rotationE?: Vec3;
  timestamp?: number;
}

export interface Velocity {
  rotation: Vec3;
  position: Vec3;
}

export interface ActorAnimation {
  object: THREE.Object3D;
  keyFrames: KeyFrame[];
}

export interface AnimationSchedule {
  name: string;
  loop?: boolean;
  noClamp?: boolean;
  timeScale?: number;
}

/* callout template example
<!--{"maxDistance":350}-->
<div style="border:1px solid red;color:white;background-color:rgba(0,0,0,0.5);padding:5px;font-size:16px;">
  Lat: {{gps.y}} <br>
  Lng: {{gps.x}} <br>
</div>
*/

export interface CalloutMetadata {
  renderer: any;
  maxDistance: number;
  arrowColor: string;
  calloutHeight: number;
  circleDistance: number;
  calloutOffset?: THREE.Vector3;
}

export class BaseActor extends EventEmitter implements ObjectStateManager {
  public states: ActorState[] = [];
  public sensors: BaseSensor[] = [];
  public robocoreClient: RobocoreClient;
  public id: string = '';
  public twist: Twist = { linear: { x: 0, y: 0, z: 0 }, angular: { x: 0, y: 0, z: 0 } };
  public calloutTemplate?: string;
  public calloutMetadata?: CalloutMetadata;
  public suppressCallout: boolean = false;
  public lastEnabledStateName: string = '';
  public maxSpeed: number = 1;
  public isSelected = false;
  public properties: any = { message: '' };
  public boundingBox?: THREE.Box3;
  public disableAnimations: boolean = false;
  public path: Vec3[] = [];
  public pathVisualizer = new ActorPathVisualizer(this);
  public get pose(): number[] {
    return [this.obj.position.x, this.obj.position.y, this.obj.position.z, this.obj.rotation.z];
  }
  
  public onMessage(message: any) {
    this.properties.message = message;
  }

  public acceptsScenarioUpdates(): boolean {
    return true; // Default implementation, can be overridden by subclasses
  }

  private hObjectMaterial: any | null = null;
  private hObject: THREE.Mesh | null = null;
  public highlightObject(objectToHighlight: string) {
    if (this.hObject) {
      this.hObject.material = this.hObjectMaterial!;
      this.hObject = null;
      this.hObjectMaterial = null;
    }
    const obj = this.obj.getObjectByName(objectToHighlight);
    if (obj) {
      this.hObject = obj as THREE.Mesh;
      this.hObjectMaterial = (obj as THREE.Mesh).material;
      this.hObject.material = new THREE.MeshBasicMaterial({
        color: 0xff0000,
      });
    }
  }
  
  public getFriendlyId(): string {
    return this.id;
  }

  public set pose(pose: number[]) {
    this.obj.position.set(pose[0], pose[1], pose[2]);
    this.obj.rotation.z = pose[3];
  }

  public getChildStateManager(name: string): ObjectStateManager | null {
    return null;
  }


  public isInSimulator(): boolean {
    const url = window.location.href;
    if (url.includes('/sim/')) {
      return true;
    }
    return false;
  }

  public allAnimations: THREE.AnimationClip[] = [];
  public mixer: THREE.AnimationMixer;
  public script: string = '';
  private animationQueue: AnimationSchedule[] = [];
  private currentAction: THREE.AnimationAction | null = null;

  constructor(public obj: THREE.Object3D, public modelCache?: GLTFResult) {
    super();
    this.robocoreClient = new RobocoreClient(this);
    this.mixer = new THREE.AnimationMixer(obj.children[0]);
    this.mixer.addEventListener("finished", (event) => {
      if (this.animationQueue.length > 0) {
        this.playNextAnime();
      }
    });
    if (modelCache) {
      this.allAnimations = modelCache.animations;
    }
    this.addState('Inactive', async () => {
      // this.disableAnimations = true;
      this.obj.visible = false;
      this.stopAnime();
      return true;
    }, async () => {
      // this.disableAnimations = false;
      this.obj.visible = true;
    }, true);

    // this.addState('Active', async () => {
    //   this.obj.visible = true;
    //   return true;
    // }, async () => {
    // }, false);

    const hideCalloutState = this.addState('Hide callout',
      async () => {
        this.suppressCallout = true;
        this.disableActorState('Show callout');
        return true;
      },
      async () => {
        // this.suppressCallout = false;
      }, false
    );
    hideCalloutState.isTriggerable = true;

    const showCalloutState = this.addState('Show callout',
      async () => {
        this.suppressCallout = false;
        this.disableActorState('Hide callout');
        return true;
      },
      async () => {
        // this.suppressCallout = true;
      }, false
    );
    showCalloutState.isTriggerable = true;    
  }

  public getActorId(): string {
    return this.id;
  }

  public async loadFromMapObject(mapObject: PosableMapObject) {
    this.script = mapObject.script;
    this.id = mapObject.id;
    this.calloutTemplate = mapObject.calloutTemplate;
    this.maxSpeed = mapObject.maxSpeed === undefined ? 1 : mapObject.maxSpeed;
  }

  public saveToMapObject(mapObject: PosableMapObject) {
    mapObject.script = this.script;
    mapObject.id = this.id;
    // transfer from actor to map object
    mapObject.calloutTemplate = this.calloutTemplate;

    mapObject.maxSpeed = this.maxSpeed;
  }



  public addState(name: string,
    onActivate: () => Promise<boolean>,
    onDeactivate: () => Promise<void>, exclusive: boolean = true) {
    const state = new ActorState(name);
    state.actor = this;
    state.exclusive = exclusive;
    state.onActivate = onActivate;
    state.onDeactivate = onDeactivate;
    this.states.push(state);
    return state;
  }

  public updateState(dgram: any) {
    Object.assign(this, dgram);
    this.emit(ActorEvents.StateChange, dgram);
  }

  public playAnimation(delta: number) {
    this.mixer.update(delta);
  }

  public sequenceAnime(...animations: AnimationSchedule[]) {
    this.animationQueue = animations;
    this.playNextAnime();
  }

  private playNextAnime() {
    if (this.animationQueue.length > 0) {
      const nextAnime = this.animationQueue.shift();
      this.playAnime(nextAnime!);
    }
  }

  public stopAnime() {
    if (this.currentAction) {
      this.currentAction.stop();
      this.currentAction.fadeOut(0.1);
    }
  }

  private playAnime(animation: AnimationSchedule) {
    const clip = this.allAnimations.find((clip) => clip.name === animation.name);
    if (clip) {
      const action = this.mixer.clipAction(clip);
      action.reset();
      if (animation.timeScale) {
        action.timeScale = animation.timeScale;
      }
      action.loop = animation.loop ? THREE.LoopRepeat : THREE.LoopOnce;
      action.clampWhenFinished = !animation.noClamp;
      action.play();
      if (this.currentAction) {
        this.currentAction.crossFadeTo(action, 0.1, true);
      }
      this.currentAction = action;
    }
  }

  public getFactory(): BaseThreeDFactory {
    return (this.obj as any).comp;
  }

  // public getUIService(): UIService {
  //   return (this.obj as any).comp.wsManagement.uiService;
  // }

  public getUIService(): UIService {
    if (!(this.obj as any)?.comp?.wsManagement?.uiService) {
      console.error("UIService is missing in BaseActor", this.obj);
      throw new Error("UIService is not available on this actor.");
    }
    return (this.obj as any).comp.wsManagement.uiService;
  }


  public async enable(state: ActorState) {
    if (!this.states.includes(state)) {
      throw new Error("State not found in actor");
    }
    const success = await state.activate();
    if (success) {
      this.emit(ActorEvents.StateChange, state);
    }
  }

  public async disable(state: ActorState) {
    if (!this.states.includes(state)) {
      throw new Error("State not found in actor");
    }
    await state.deactivate();
    this.emit(ActorEvents.StateChange, state);
  }

  public isEnabled(stateName: string): boolean {
    const state = this.states.find((s) => s.name === stateName);
    return state?.active || false;
  }

  public async enableActorState(stateName: string) {
    this.lastEnabledStateName = stateName;
    const state = this.states.find((s) => s.name === stateName);
    if (state) {
      await this.enable(state);
    }
  }

  public async disableActorState(stateName: string) {
    const state = this.states.find((s) => s.name === stateName);
    if (state) {
      await this.disable(state);
    }
  }

  public getCameraConfig(): CameraConfig | null {
    return null;
  }

  public onSelected(on:boolean) {
    this.isSelected = on;
  }

  protected renderSensors(delta: number) {
    for (const sensor of this.sensors) {
      if (sensor.isOn) {
        sensor.render(delta);
      }
    }
  }
  private nextUpdateTime: number = 0;
  public render(delta: number) {
    this.playAnimation(delta);
    if (this.nextUpdateTime < Date.now()) {
      this.renderSensors(delta);
      this.nextUpdateTime = Date.now() + 100; // Update every 100ms
    }
    this.obj.position.addScaledVector(new THREE.Vector3(1, 0, 0), this.twist.linear.x * delta);
    this.obj.position.addScaledVector(new THREE.Vector3(0, 1, 0), this.twist.linear.y * delta);
    this.obj.position.addScaledVector(new THREE.Vector3(0, 0, 1), this.twist.linear.z * delta);
    this.obj.rotation.z += this.twist.angular.z * delta;
    this.obj.rotation.z = normalizeAngleToPi(this.obj.rotation.z);
  }

  public dispose() {
    this.removeAllListeners();
  }

}
